import React from 'react';

import moment, { Moment } from 'moment';

import { Box, Typography } from '@material-ui/core';
import { Tooltip } from '@mui/material';
import { MobileDateTimePicker } from '@mui/x-date-pickers';
import { DatePicker } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';

import { useStyles } from '../../pages/reports/Themable.hooks';

import { useUserSettings } from '../../state/user/index.hooks';

import TranslationHelper from '../../helpers/TranslationHelper';

import TimeRangeButtons from '../TimeRangeButtons';
import { IErrors } from '../../pages/reports/components/GeneratedReports/components/ReportsForm/ReportsForm';
import InformationIcon from '@material-ui/icons/Info';

interface IOwnProps {
    dateFrom: Moment;
    dateTo: Moment;
    errors?: IErrors;
    title?: string;
    tooltip?: string;
    showButtons?: boolean;
    dateOnly?: boolean;
    disablePast?: boolean;
    timeRangeDays?: number;
    handleFormChange: <T>(name: string, value: T) => void;
}
const DatePresets = ({
    dateFrom,
    dateTo,
    errors,
    title = 'Date preset',
    tooltip,
    showButtons = true,
    disablePast = false,
    dateOnly,
    timeRangeDays,
    handleFormChange,
}: IOwnProps) => {
    const classes = useStyles();
    const userSettings = useUserSettings();

    const dateTranslations = TranslationHelper.makeDateTimeTranslations();

    const buttons = [
        {
            onClick: () => handleChangeDate(moment(), moment()),
            text: 'Today',
        },
        {
            onClick: () =>
                handleChangeDate(
                    moment().subtract(1, 'day'),
                    moment().subtract(1, 'day')
                ),
            text: 'Yesterday',
        },
        {
            onClick: () =>
                handleChangeDate(
                    moment().subtract(1, 'week'),
                    moment().subtract(1, 'day')
                ),
            text: 'Last week',
        },
    ];

    const handleChangeDate = (dateFrom: Moment, dateTo: Moment) => {
        handleChangeFromDate(dateFrom.clone().startOf('day'));
        handleChangeToDate(dateTo.clone().endOf('day'));
    };

    const handleChangeFromDate = (date: MaterialUiPickersDate) => {
        handleFormChange('dateFrom', date);

        if (date && timeRangeDays) {
            const maxDateTo = moment();
            const adjustedMaxDateTo = moment.min(
                date.clone().add(timeRangeDays, 'days'),
                maxDateTo
            );
            handleFormChange('dateTo', adjustedMaxDateTo);
        }
    };

    const handleChangeToDate = (date: MaterialUiPickersDate) => {
        handleFormChange('dateTo', date);
    };

    const commonPickersProps = {
        fullWidth: true,
        required: true,
        className: classes.field,
        ...dateTranslations,
    };

    const calculateAdjustedDateLimits = () => {
        const maxDateTo = moment();
        const maxDateFrom =
            timeRangeDays && dateTo
                ? moment.min(dateTo, moment())
                : dateTo || moment();
        const minDateTo = dateFrom;
        const adjustedMaxDateTo =
            timeRangeDays && dateFrom
                ? moment.min(
                      dateFrom.clone().add(timeRangeDays, 'days'),
                      maxDateTo
                  )
                : undefined;
        return { maxDateFrom, minDateTo, adjustedMaxDateTo };
    };

    const renderDatePickers = () => {
        const { maxDateFrom, minDateTo, adjustedMaxDateTo } =
            calculateAdjustedDateLimits();

        return (
            <>
                <DatePicker
                    error={!!errors?.dateFrom}
                    helperText={errors?.dateFrom}
                    name="dateFrom"
                    value={dateFrom}
                    maxDate={maxDateFrom}
                    onChange={handleChangeFromDate}
                    format={userSettings.shortDateFormat}
                    label={TranslationHelper.translate('From')}
                    autoOk={true}
                    variant="inline"
                    disablePast={disablePast}
                    {...commonPickersProps}
                />
                <DatePicker
                    error={!!errors?.dateTo}
                    helperText={errors?.dateTo}
                    name="dateTo"
                    value={dateTo}
                    minDate={minDateTo}
                    maxDate={adjustedMaxDateTo}
                    onChange={handleChangeToDate}
                    format={userSettings.shortDateFormat}
                    label={TranslationHelper.translate('To')}
                    autoOk={true}
                    variant="inline"
                    {...commonPickersProps}
                />
            </>
        );
    };

    const renderDateTimePickers = () => {
        const { maxDateFrom, minDateTo, adjustedMaxDateTo } =
            calculateAdjustedDateLimits();

        return (
            <Box
                sx={{ marginTop: 10, display: 'flex', flexDirection: 'column' }}
            >
                <MobileDateTimePicker
                    views={[
                        'year',
                        'month',
                        'day',
                        'hours',
                        'minutes',
                        'seconds',
                    ]}
                    name="dateFrom"
                    value={dateFrom}
                    maxDate={maxDateFrom}
                    onChange={handleChangeFromDate}
                    label={TranslationHelper.translate('From')}
                    format={userSettings.dateFormat}
                    ampm={false}
                    localeText={dateTranslations}
                    slots={{}}
                    slotProps={{
                        textField: {
                            fullWidth: true,
                            variant: 'standard',
                            error: !!errors?.dateFrom,
                            helperText: errors?.dateFrom,
                        },
                    }}
                />

                <MobileDateTimePicker
                    views={[
                        'year',
                        'month',
                        'day',
                        'hours',
                        'minutes',
                        'seconds',
                    ]}
                    name="dateTo"
                    value={dateTo}
                    maxDate={adjustedMaxDateTo}
                    minDate={minDateTo}
                    onChange={handleChangeToDate}
                    localeText={dateTranslations}
                    label={TranslationHelper.translate('To')}
                    format={userSettings.dateFormat}
                    ampm={false}
                    slotProps={{
                        textField: {
                            fullWidth: true,
                            variant: 'standard',
                            error: !!errors?.dateTo,
                            helperText: errors?.dateTo,
                        },
                    }}
                />
            </Box>
        );
    };
    return (
        <>
            <Box className={classes.tooltipBox}>
                {tooltip && (
                    <Tooltip
                        title={TranslationHelper.translate(tooltip)}
                        placement="bottom-start"
                    >
                        <InformationIcon className={classes.tooltipIcon} />
                    </Tooltip>
                )}
                <Typography color={'primary'} className={classes.typography}>
                    {TranslationHelper.translate(title)}
                </Typography>
            </Box>
            {showButtons && <TimeRangeButtons buttons={buttons} />}
            {dateOnly ? renderDatePickers() : renderDateTimePickers()}
        </>
    );
};

export default DatePresets;
